import clsx from 'clsx'
import { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import GeoContext from 'contexts/geoContext'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import type { CreateTripItemData } from 'tripPlanner/api/tripItem/types'
import MultiTypeItemBookingCard from 'tripPlanner/components/Bookings/MultiTypeItemBookingCard'
import SingleTypeItemBookingCard from 'tripPlanner/components/Bookings/SingleTypeItemBookingCard'
import { useCreateTripItemBatch } from 'tripPlanner/hooks/api/tripItem'
import { useOrdersByMemberId } from 'tripPlanner/hooks/api/order'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { convertTripItemsFromOrderItems } from 'tripPlanner/utils/itemConversions/tripItemFromOrderItems'
import { orderHasMultiItemTypes } from 'tripPlanner/utils/order'
import { getFutureFilteredOrdersNotInTrip } from 'tripPlanner/utils/summary'
import { rem } from 'polished'
import { scrollToNewItemWithRetry } from 'tripPlanner/utils/scrollToNewItem'

type OrderAndOffers = {
  order: App.Order
  offers: Array<OfferTypeForTripItemTypes>
}

interface Props {
  tripId: string
}

const ListOfOrders = styled.ul`
  li {
    display: flex;
    align-items: center;
    gap: ${rem(12)};
    margin-top: ${rem(12)};
    border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
    border-radius: ${(props) => props.theme.borderRadius.S};

    .order-details {
      flex: 1;
    }

    &.isPackage {
      align-items: flex-start;
    }

    &:first-child {
      margin-top: 0;
    }

    &:hover,
    &.selected {
      border: 1px solid ${(props) => props.theme.palette.brand.primary.normal};
    }

    &:empty {
      display: none;
    }

    & > div:empty {
      display: none;
    }
  }
`

function SelectBookingToAdd(props: Props) {
  const intl = useIntl()
  const { tripId } = props
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)

  const { memberId } = useAppSelector(
    (state) => ({
      memberId: state.auth.account.memberId,
    }),
    shallowEqual,
  )

  const { data: trip, isLoading: isTripLoading } = useTrip({ tripId })

  const { closeModal, goBack } = useModalCallbacks()

  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [orderAndOffers, setOrderAndOffers] = useState<Array<OrderAndOffers>>(
    [],
  )

  const { data: orders, isLoading: isOrdersLoading } = useOrdersByMemberId(
    currentRegionCode,
    memberId,
  )

  const closeCurrentModal = useCallback(() => {
    closeModal()
  }, [closeModal])

  const filteredOrders = useMemo(
    () => getFutureFilteredOrdersNotInTrip(orders, trip),
    [orders, trip],
  )

  const { mutate: createMutate, isLoading: isCreateLoading } =
    useCreateTripItemBatch({
      onSuccess: (result) => {
        scrollToNewItemWithRetry(result.updatedItemIds[0])
        closeModal()
      },
      onSettled: () => {
        setIsSaving(false)
      },
    })

  const handleOnSelect = useCallback(
    (
      order: App.Order,
      offers: Array<OfferTypeForTripItemTypes>,
      checked: boolean,
    ) => {
      if (checked) {
        setOrderAndOffers([
          ...orderAndOffers,
          {
            order,
            offers,
          },
        ])
      } else {
        setOrderAndOffers(
          orderAndOffers.filter(
            (orderAndOffer) => orderAndOffer.order.id != order.id,
          ),
        )
      }
    },
    [orderAndOffers],
  )

  const selectedOrderIds = orderAndOffers.map(
    (orderAndOffer) => orderAndOffer.order.id,
  )

  const isLoadingInProgress =
    isCreateLoading || isSaving || isOrdersLoading || isTripLoading

  const handlePrimaryActionClick = useCallback(async() => {
    setIsSaving(true)

    const createItemRequestPromises: Array<
      Promise<Array<CreateTripItemData>>
    > = orderAndOffers.map((orderAndOffer) =>
      convertTripItemsFromOrderItems({
        order: orderAndOffer.order,
        offers: orderAndOffer.offers,
        currentRegionCode,
        currentCurrency,
      }),
    )

    const allGroupedItemsPerOder = await Promise.all(createItemRequestPromises)

    const finalCreateTripItemReqsArr: Array<CreateTripItemData> = []
    allGroupedItemsPerOder.map((allGroupedItemsPerOderCreatRequestArr) => {
      finalCreateTripItemReqsArr.push(...allGroupedItemsPerOderCreatRequestArr)
    })

    createMutate({ tripId, tripItems: finalCreateTripItemReqsArr })
  }, [createMutate, currentCurrency, currentRegionCode, orderAndOffers, tripId])

  const modalTitle = intl.formatMessage({
    id: 'IdzROLFsTNE1vOMWrIqd',
    defaultMessage: 'Add bookings to trip',
    description: 'Title for add bookings to trip modal in the trip planner feature',
  })

  const allBookingsAddedHeading = intl.formatMessage({
    id: 'gDY2_f6eQcI3zDZfaedY',
    defaultMessage: 'All existing bookings added...',
    description: 'Heading when all bookings are already added in the booking flow',
  })

  const allBookingsAddedDescription = intl.formatMessage({
    id: 'QOuQaiIpDwHJpP3gT8QC',
    defaultMessage: 'Any bookings not already in the trip will appear here.',
    description: 'Description when all bookings are already added in the trip planner item editor modal',
  })

  const continueButtonText = intl.formatMessage({
    id: 'w0bZO-QKmPHt1LUqlgcZ',
    defaultMessage: 'Continue',
    description: 'Button text to continue to the next step in the current multi-step process',
  })

  const cancelButtonText = intl.formatMessage({
    id: '0h3WSfyICL6Yx-LmqJ1k',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel the current action and dismiss the dialog or modal',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          {isLoadingInProgress && <LoadingIndicator inline />}
          {!isLoadingInProgress && (
            <ListOfOrders>
              {filteredOrders.map((upcomingOrder: App.Order) => {
                const orderHasMultiItems = orderHasMultiItemTypes(upcomingOrder)

                return (
                  <li
                    key={upcomingOrder.id}
                    className={clsx({
                      isPackage: orderHasMultiItems,
                      selected: selectedOrderIds.includes(upcomingOrder.id),
                    })}
                  >
                    <VerticalSpacer gap={24} className="order-details">
                      {orderHasMultiItems && (
                        <MultiTypeItemBookingCard
                          onSelect={handleOnSelect}
                          order={upcomingOrder}
                        />
                      )}
                      {!orderHasMultiItems && (
                        <SingleTypeItemBookingCard
                          onSelect={handleOnSelect}
                          order={upcomingOrder}
                        />
                      )}
                    </VerticalSpacer>
                  </li>
                )
              })}
            </ListOfOrders>
          )}
          {!isLoadingInProgress && filteredOrders.length === 0 && (
            <>
              <Heading variant="heading6">
                {allBookingsAddedHeading}
              </Heading>
              <BodyText variant="large">
                {allBookingsAddedDescription}
              </BodyText>
            </>
          )}
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: continueButtonText,
          disabled: orderAndOffers.length === 0 || isCreateLoading || isSaving,
          onClick: handlePrimaryActionClick,
        }}
        secondaryActionProps={{
          children: cancelButtonText,
          onClick: closeCurrentModal,
        }}
      />
    </>
  )
}

export default SelectBookingToAdd
